import React from 'react';
import { BlogLayout, H2, H3, P, UL, InfoBox, ScoreCard } from './BlogLayout';

const Blog2 = () => (
  <BlogLayout
    title="What Are HTTP Security Headers and Why They Matter"
    description="CSP, HSTS, X-Frame-Options — these headers protect your site from XSS, clickjacking, and data theft. Here's what each one does and how to check yours."
    date="April 2026"
    readTime={8}
    category="Technical Guide"
  >
    <P>
      HTTP security headers are one of the most overlooked aspects of website security. They're invisible — visitors never see them — but they're the first line of defence against some of the most common web attacks. Most websites, including well-known ones, are missing several of these headers.
    </P>
    <P>
      This guide explains what each header does in plain English, what attack it prevents, and how to add it to your site.
    </P>

    <H2>What Are HTTP Headers?</H2>
    <P>
      When your browser requests a web page, the server sends back not just the HTML content but also a set of "headers" — metadata about the response. These headers tell the browser how to behave: whether to cache the page, what language it's in, and crucially — what security rules to follow.
    </P>
    <InfoBox title="How to view your site's current headers" color="#00F0FF">
      Open Chrome → press F12 → go to Network tab → reload the page → click on the first request → look at the Response Headers section. You'll see which security headers your server currently sends.
    </InfoBox>

    <H2>The 6 Headers Every Website Needs</H2>

    <H3>1. Content-Security-Policy (CSP)</H3>
    <P>
      <strong style={{ color: '#fff' }}>What it prevents:</strong> Cross-Site Scripting (XSS) attacks — where attackers inject malicious scripts into your pages.
    </P>
    <P>
      CSP is the most powerful security header. It tells the browser exactly which sources of content are trusted. If an attacker manages to inject a script from an untrusted source, the browser simply refuses to run it.
    </P>
    <InfoBox title="Example CSP header" color="#00FF94">
      <code style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
        Content-Security-Policy: default-src 'self'; script-src 'self' https://checkout.razorpay.com
      </code>
      <p style={{ marginTop: 8, marginBottom: 0 }}>This only allows scripts from your own domain and Razorpay's checkout. Any injected script from another source gets blocked.</p>
    </InfoBox>
    <P>
      CSP can be complex to configure — too restrictive and your site breaks; too permissive and it's ineffective. Start with a report-only mode to see what would be blocked before enforcing it.
    </P>

    <H3>2. Strict-Transport-Security (HSTS)</H3>
    <P>
      <strong style={{ color: '#fff' }}>What it prevents:</strong> Protocol downgrade attacks and cookie hijacking over HTTP.
    </P>
    <P>
      HSTS tells browsers to always use HTTPS when connecting to your site — even if a user types just "example.com" without the https://. Without HSTS, an attacker on the same network can intercept the initial HTTP connection before the redirect happens.
    </P>
    <InfoBox title="Recommended HSTS header" color="#00FF94">
      <code style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
        Strict-Transport-Security: max-age=31536000; includeSubDomains; preload
      </code>
      <p style={{ marginTop: 8, marginBottom: 0 }}>This enforces HTTPS for 1 year, applies to all subdomains, and opts you into browser preload lists.</p>
    </InfoBox>

    <H3>3. X-Frame-Options</H3>
    <P>
      <strong style={{ color: '#fff' }}>What it prevents:</strong> Clickjacking — where attackers embed your site in an invisible iframe and trick users into clicking buttons they can't see.
    </P>
    <P>
      A classic clickjacking attack overlays an invisible version of your site on top of a fake page. A user thinks they're clicking "Win a Prize" but they're actually clicking "Transfer ₹10,000" on your banking site.
    </P>
    <ScoreCard label="DENY" score="No site can embed yours" color="#00FF94" />
    <ScoreCard label="SAMEORIGIN" score="Only your own domain can embed yours" color="#FFD600" />
    <ScoreCard label="Missing" score="Any site can embed yours in an iframe" color="#FF0055" />

    <H3>4. X-Content-Type-Options</H3>
    <P>
      <strong style={{ color: '#fff' }}>What it prevents:</strong> MIME-type sniffing attacks.
    </P>
    <P>
      Browsers try to "guess" what type of file they're loading if the server doesn't specify. Attackers can exploit this by uploading a file that looks like an image but contains JavaScript. This header turns off that guessing.
    </P>
    <InfoBox title="Header value" color="#00F0FF">
      <code style={{ fontFamily: 'monospace', fontSize: 12 }}>X-Content-Type-Options: nosniff</code>
      <p style={{ marginTop: 8, marginBottom: 0 }}>Always use nosniff. There's no valid reason to leave this out.</p>
    </InfoBox>

    <H3>5. Referrer-Policy</H3>
    <P>
      <strong style={{ color: '#fff' }}>What it prevents:</strong> Leaking sensitive URL information to third-party sites.
    </P>
    <P>
      When a user clicks a link from your site to an external site, the browser sends a "Referer" header telling the external site where the user came from. If your URLs contain sensitive information (like user IDs, search queries, or reset tokens), this leaks that data to every external site you link to.
    </P>
    <P>
      Recommended value: <code style={{ background: '#1a1a3e', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>Referrer-Policy: strict-origin-when-cross-origin</code>
    </P>

    <H3>6. Permissions-Policy</H3>
    <P>
      <strong style={{ color: '#fff' }}>What it prevents:</strong> Malicious scripts accessing powerful browser features without your knowledge.
    </P>
    <P>
      This header controls which browser features your site can use — camera, microphone, geolocation, etc. If a third-party script you load (like an analytics tool or ad network) gets compromised, this header limits what it can do.
    </P>
    <P>
      Example: <code style={{ background: '#1a1a3e', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>Permissions-Policy: camera=(), microphone=(), geolocation=()</code> — disables all three for everyone.
    </P>

    <H2>How to Add These Headers</H2>
    <H3>On Nginx</H3>
    <InfoBox title="nginx.conf" color="#7000FF">
      <code style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', display: 'block', wordBreak: 'break-all' }}>
{`add_header X-Frame-Options "SAMEORIGIN";
add_header X-Content-Type-Options "nosniff";
add_header Referrer-Policy "strict-origin-when-cross-origin";
add_header Strict-Transport-Security "max-age=31536000; includeSubDomains" always;`}
      </code>
    </InfoBox>
    <H3>On Apache</H3>
    <InfoBox title=".htaccess" color="#7000FF">
      <code style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', display: 'block', wordBreak: 'break-all' }}>
{`Header always set X-Frame-Options "SAMEORIGIN"
Header always set X-Content-Type-Options "nosniff"
Header always set Referrer-Policy "strict-origin-when-cross-origin"
Header always set Strict-Transport-Security "max-age=31536000"`}
      </code>
    </InfoBox>
    <H3>On Vercel (vercel.json)</H3>
    <InfoBox title="vercel.json" color="#7000FF">
      <code style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', display: 'block', wordBreak: 'break-all' }}>
{`{
  "headers": [
    {
      "source": "/(.*)",
      "headers": [
        { "key": "X-Frame-Options", "value": "SAMEORIGIN" },
        { "key": "X-Content-Type-Options", "value": "nosniff" },
        { "key": "Referrer-Policy", "value": "strict-origin-when-cross-origin" }
      ]
    }
  ]
}`}
      </code>
    </InfoBox>

    <H2>Check Your Headers Right Now</H2>
    <P>
      Run a WebShield scan on your domain. The security headers check instantly shows you which of the 6 headers are present, which are missing, and rates the severity of each missing one. The PDF report includes exactly what to add and where.
    </P>
    <P>
      Most sites can go from 0 to 5 headers implemented in under an hour — with no code changes, just server configuration updates.
    </P>
  </BlogLayout>
);

export default Blog2;